"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, House as Home, Hammer, SunMedium, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const serviceCards = [{
  id: "commercial",
  title: "Commercial scaffolding",
  description: "Access systems, temporary roofs, and edge protection built to TG20:21 standards for main contractors.",
  to: "/CommercialScaffoldingPage",
  icon: Building2,
  badge: "Developers & Main Contractors"
}, {
  id: "residential",
  title: "Residential scaffolding",
  description: "Tidy, secure domestic access for home extensions, chimney works, rendering, and repairs.",
  to: "/ResidentialScaffoldingPage",
  icon: Home,
  badge: "Homeowners & Builders"
}, {
  id: "roofing",
  title: "Roofing scaffolding",
  description: "Dedicated roof-level platforms and perimeter guardrails configured for rapid roof replacements.",
  to: "/RoofingScaffoldingPage",
  icon: Hammer,
  badge: "Roofing Contractors"
}, {
  id: "solar",
  title: "Solar scaffolding",
  description: "Fast-turnaround solar access scaffolds and safe loading bays for renewable energy teams.",
  to: "/SolarScaffoldingPage",
  icon: SunMedium,
  badge: "Solar PV Installers"
}];
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="keyservicesgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-3">
              <Badge variant="outline" className="px-3 py-1 font-medium text-xs">
                <ShieldCheck className="size-3.5 text-primary" />
                NASC & CHAS Accreditations
              </Badge>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-default tracking-tight text-foreground">
              Core scaffolding services
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground">
              Direct access solutions engineered for safety, speed, and strict site compliance across the South East.
            </p>
          </div>

          <div className="shrink-0">
            <Link to="/ContactRequestQuotePage" className="inline-flex items-center text-sm font-semibold text-primary hover:underline group">
              <span>Request a site visit</span>
              <ArrowRight className="ml-1.5 size-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>

        {/* 4-card Grid: 2 columns on mobile/tablet, 4 on desktop */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {serviceCards.map((service, i) => {
          const Icon = service.icon;
          return <motion.div key={service.id} variants={cardVariants} data-index={i} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/50 group py-5 sm:py-6">
                  <CardContent className="flex flex-col h-full justify-between p-0 px-5 sm:px-6">
                    <div>
                      {/* Icon header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="size-11 sm:size-12 rounded-lg bg-accent flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                          <Icon className="size-5 sm:size-6" />
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">
                          0{i + 1}
                        </span>
                      </div>

                      {/* Badge / Audience */}
                      <div className="mb-2">
                        <span className="text-2xs sm:text-xs font-medium text-muted-foreground uppercase tracking-wider block truncate">
                          {service.badge}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-lg sm:text-xl font-bold font-default text-card-foreground group-hover:text-primary transition-colors duration-200">
                        {service.title}
                      </h3>

                      {/* Description */}
                      <p className="mt-2 text-xs sm:text-sm text-muted-foreground line-clamp-3">
                        {service.description}
                      </p>
                    </div>

                    {/* Action Link */}
                    <div className="pt-6 mt-4 border-t border-border/60">
                      <Link to={service.to} className="inline-flex items-center justify-between w-full text-xs sm:text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                        <span>View service</span>
                        <div className="size-6 rounded-full bg-accent flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <ArrowRight className="size-3.5" />
                        </div>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}